import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateAttribute, IsFlowDeployed } from "../../../../actions/flowActions";
import { GetActivationDestinationSchedules, GetScheduleTemplates } from "../../../../actions/companiesActions";
import { requestEditSchedule } from "../../../../actions/scheduleActions";
import { notifyError } from "../../../../actions/notifyActions";
import { FormControl, InputLabel, ListSubheader, Select, MenuItem, Divider, Button, Chip } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import { IAppState } from "../../../../types/stores";
import { FlowExport } from "../../../../types/stores/flowTypes";
import { Dialog } from "@material-ui/core";
import ScheduleAddEdit from "../../../schedule/schedules/ScheduleAddEdit";
import { IAdvertisersByCompany } from "../../../../types/stores/vars";
import RecurrencePopup, { RecurrenceConfig } from "./RecurrencePopup";
import { ISchedule, IScheduleTemplate } from "../../../../types/stores/schedules";
import Moment from "moment";

type Props = {
    flowExport: FlowExport;
    destination: IAdvertisersByCompany;
    canEdit: boolean;
};

// Derive a human-readable summary chip label from a schedule record.
export const buildScheduleSummary = (schedule: ISchedule | IScheduleTemplate): string => {
    if (!schedule) return "";

    const scheduleName = "Name" in schedule ? schedule.Name : "ScheduleName" in schedule ? schedule.ScheduleName : "";

    // Monthly recurrence schedule created via the recurrence popup (Name matches "Refresh on day X at HH:MM every month").
    // These schedules may not have ScheduleType/TimeType/TimeCount set, so parse the CronString directly.
    if (
        "CronString" in schedule &&
        schedule.CronString &&
        /^Refresh on day \d+ at \d{2}:\d{2} every month(\s*\(.*\))?$/i.test(scheduleName)
    ) {
        const parts = schedule.CronString.split(" ");
        if (parts.length >= 5) {
            const minute = parseInt(parts[0], 10);
            const hour = parseInt(parts[1], 10);
            const dayOfMonth = parseInt(parts[2], 10);
            if (!isNaN(minute) && !isNaN(hour) && !isNaN(dayOfMonth)) {
                const time = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
                const endPart = schedule.EndDate
                    ? `Ends ${Moment(schedule.EndDate).format("MM/DD/YYYY")}`
                    : "No end date";
                return `Refreshes on day ${dayOfMonth} at ${time} every month \u2022 ${endPart}`;
            }
        }
    }
    // TimeFrequency type (scheduleType == 6): TimeCount + TimeType
    if ("ScheduleType" in schedule && schedule.ScheduleType === 6 && schedule.TimeCount && schedule.TimeType) {
        const endPart = schedule.EndDate
            ? `Ends ${Moment(schedule.EndDate).format("MM/DD/YYYY")}`
            : "Ends per Expiration";

        const unitMap: Record<number, string> = { 1: "day", 2: "week", 3: "month" };
        const unit = unitMap[schedule.TimeType] ?? "day";
        const count = schedule.TimeCount === 1 ? "" : `${schedule.TimeCount} `;
        const unitStr = schedule.TimeCount === 1 ? unit : `${unit}s`;
        return `Refresh every ${count}${unitStr} \u2022 ${endPart}`;
    }

    // IScheduleTemplate with FrequencyDays (e.g., "Refresh Every 30 Days")
    if ("FrequencyDays" in schedule && schedule.FrequencyDays && !schedule.IsOneTime) {
        const days = schedule.FrequencyDays;
        const count = days === 1 ? "" : `${days} `;
        const unitStr = days === 1 ? "day" : "days";
        return `Refresh every ${count}${unitStr} \u2022 Ends per Expiration`;
    }

    // One-time (scheduleType == 1 for ISchedule, IsOneTime for IScheduleTemplate)
    if ("ScheduleType" in schedule && schedule.ScheduleType === 1) {
        return "Runs once at next window \u2022 No refresh.";
    }
    if ("IsOneTime" in schedule && schedule.IsOneTime) {
        return "Runs once at next window \u2022 No refresh.";
    }

    // Fallback: use the schedule name (ISchedule has Name, IScheduleTemplate has ScheduleName)
    return "Name" in schedule ? schedule.Name : schedule.ScheduleName;
};

// Default template name to pre-select when no schedule has been chosen yet.
const MONTHLY_TEMPLATE_NAME = "Refresh on the X of every month";
const NIL_UUID = "00000000-0000-0000-0000-000000000000";

const ActivationDestinationSchedules: React.FC<Props> = ({ flowExport, canEdit }: Props) => {
    const dispatch = useDispatch();

    const flow = useSelector((state: IAppState) => state.flows.byId[state.selected.flow]);
    const activationDestinationSchedules = useSelector((state: IAppState) => state.vars.activationDestinationSchedules);
    const rawScheduleTemplates = useSelector((state: IAppState) => state.vars.scheduleTemplates);
    const scheduleTemplates = useMemo(() => rawScheduleTemplates || [], [rawScheduleTemplates]);
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures);
    const selectedSchedule = activationDestinationSchedules
        ? activationDestinationSchedules.find(x => x.Id == flow?.ScheduleId)
        : null;
    // In new behavior, also check scheduleTemplates for the selected value.
    const selectedTemplate = Array.isArray(scheduleTemplates)
        ? scheduleTemplates.find(x => x.Id == flow?.ScheduleId)
        : undefined;
    const currentSelection = selectedSchedule ?? selectedTemplate;

    const [showEditScheduleModal, setEditScheduleModal] = useState(false);
    const useScheduleTemplates = enabledFeatures.includes("schedule-templates");
    const [isDeployed, setIsDeployed] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [showRecurrence, setShowRecurrence] = useState(false);

    useEffect(() => {
        if (flowExport.DestinationId && flow?.FlowId) {
            dispatch(GetActivationDestinationSchedules(flowExport.DestinationId, flow.FlowId));
        }
    }, [flow?.isSaving, flow?.FlowId, flowExport.DestinationId, dispatch]);

    // Fetch schedule templates from the dedicated endpoint (new behavior only).
    useEffect(() => {
        const fetchData = async () => {
            if (useScheduleTemplates) {
                dispatch(GetScheduleTemplates());
                const deployedResult = await (dispatch as any)(IsFlowDeployed(flow.FlowId));
                setIsDeployed(deployedResult);
            }
        };
        fetchData();
    }, [useScheduleTemplates, dispatch, flow.FlowId]);

    // If using schedule templates and there is no schedule assigned to the flow, assign the default template.
    useEffect(() => {
        if (useScheduleTemplates && scheduleTemplates.length > 0) {
            if (flow.ScheduleId == null) {
                dispatch(updateAttribute("flows", flow?.FlowId, "ScheduleId", NIL_UUID));
            }
        }
    }, [scheduleTemplates, useScheduleTemplates, flow?.ScheduleId, flow?.FlowId, dispatch]);

    useEffect(() => {
        if (currentSelection && flow && flow.ScheduleId != currentSelection.Id) {
            dispatch(updateAttribute("flows", flow?.FlowId, "ScheduleId", currentSelection?.Id));
        }
    }, [currentSelection, flow?.FlowId, flow?.ScheduleId, dispatch]);

    // Build initial recurrence config from the current selection for the popup.
    const buildInitialRecurrenceConfig = (): Partial<RecurrenceConfig> | undefined => {
        if (!currentSelection) return undefined;

        // Try to derive day-of-month, hour, and minute from the cron string if available
        if ("CronString" in currentSelection && currentSelection.CronString) {
            const parts = currentSelection.CronString.split(" ");
            if (parts.length >= 5) {
                const minute = parseInt(parts[0], 10);
                const hour = parseInt(parts[1], 10);
                const dayOfMonth = parseInt(parts[2], 10);
                if (!isNaN(minute) && !isNaN(hour) && !isNaN(dayOfMonth)) {
                    return { dayOfMonth, hour, minute };
                }
            }
        }

        return undefined;
    };

    const isScheduleTemplateSelection = (
        selection?: ISchedule | IScheduleTemplate | null
    ): selection is IScheduleTemplate => !!selection && "ScheduleName" in selection && !("CronString" in selection);

    const handleRecurrenceApply = (config: RecurrenceConfig) => {
        setShowRecurrence(false);
        const cronString = `${config.minute} ${config.hour} ${config.dayOfMonth} * *`;
        const name = `Refresh on day ${config.dayOfMonth} at ${String(config.hour).padStart(2, "0")}:${String(config.minute).padStart(2, "0")} every month`;
        const scheduleId = isScheduleTemplateSelection(currentSelection) ? "" : flow?.ScheduleId || "";

        dispatch(
            requestEditSchedule(
                scheduleId,
                `${name}`,
                cronString,
                null, // timezone
                3, // scheduleType = EndDate, so SaveSchedule syncs end date from driver field expiration
                "", // runDate
                selectedSchedule?.EndDate ?? Moment().add(6, "months").format("MM/DD/YYYY"),
                null, // nOccurrences
                null, // automationIdSelected
                false, // isConsecutive
                false, // continueOnError
                false, // isActive
                false, // isScheduleTemplate
                true, // isNameSyncWithFlow
                flow?.CompanyId ?? null,
                "FLOW_DEPLOY", // defaultTriggerType
                null, // emails
                flow?.FlowId ?? null,
                null, // TimeType = Month
                null, // TimeCount = 1 (every 1 month)
                (savedId: string) => {
                    activationDestinationScheduleChanged(savedId);
                    if (flowExport.DestinationId && flow?.FlowId) {
                        dispatch(GetActivationDestinationSchedules(flowExport.DestinationId, flow.FlowId));
                    }
                }
            )
        );
    };

    const activationDestinationScheduleChanged = (newValue: string) => {
        if (!flow?.FlowId) return;
        dispatch(updateAttribute("flows", flow?.FlowId, "ScheduleId", newValue));
    };

    const hide = () => {
        setEditScheduleModal(false);
    };

    // Auto-open recurrence popup when the template or a user-created monthly-day schedule is selected.
    const handleTemplateScheduleChange = (newValue: string) => {
        const selectedT = (scheduleTemplates as IScheduleTemplate[]).find(s => s.Id === newValue);
        if (selectedT?.ScheduleName === MONTHLY_TEMPLATE_NAME) {
            if ((flow?.FlowId ?? 0) <= 0) {
                dispatch(notifyError("Please save your flow before creating a schedule"));
                const defaultTemplate = scheduleTemplates.find(s => s.IsDefault && s.ScheduleName === NIL_UUID);
                if (defaultTemplate) {
                    activationDestinationScheduleChanged(defaultTemplate.Id);
                }
                return;
            }
            if (useScheduleTemplates) {
                setShowRecurrence(true);
            } else if (enabledFeatures.includes("edit-activation-schedules-modal")) {
                setShowModal(true);
            }
        }
        activationDestinationScheduleChanged(newValue);
    };

    const openEditScheduleModal = () => {
        setEditScheduleModal(true);
    };

    const handleScheduleSaved = (id: string) => {
        hide();
        activationDestinationScheduleChanged(id);
        if (flowExport.DestinationId && flow?.FlowId) {
            dispatch(GetActivationDestinationSchedules(flowExport.DestinationId, flow.FlowId));
        }
    };

    const renderEditScheduleModal = () => (
        <Dialog id="schedule-modal" open={showEditScheduleModal} onClose={hide}>
            <ScheduleAddEdit
                scheduleId={flow && "ScheduleId" in flow ? flow.ScheduleId : ""}
                companyId={flow?.CompanyId}
                isScheduleTemplate={selectedSchedule?.IsScheduleTemplate}
                isExportModal={true}
                onSaveCallback={handleScheduleSaved}
            />
        </Dialog>
    );

    const renderSchedules = () => {
        const schedules = activationDestinationSchedules.filter(x => !x.IsScheduleTemplate && x.IsActive);
        const templates = activationDestinationSchedules.filter(x => x.IsScheduleTemplate && x.IsActive);
        return (
            <>
                <InputLabel htmlFor="activation-destination-select">Refresh Schedule</InputLabel>
                <Select
                    defaultValue=""
                    id="activation-destination-select"
                    value={flow?.ScheduleId || ""}
                    disabled={!canEdit || !!(flow && flow.IsLocked)}
                    fullWidth
                    onChange={e => activationDestinationScheduleChanged(e.target.value as string)}
                >
                    {schedules.length > 0 && (
                        <ListSubheader onClick={e => e.stopPropagation()}>Schedules assigned to flow</ListSubheader>
                    )}
                    {schedules.length > 0 && <Divider />}
                    {schedules.map(s => (
                        <MenuItem key={s.Id} value={s.Id} selected={s.Id == flow?.ScheduleId}>
                            {s.Name}
                        </MenuItem>
                    ))}
                    <ListSubheader onClick={e => e.stopPropagation()}>Schedule templates available</ListSubheader>
                    <Divider />
                    {templates.map(s => (
                        <MenuItem key={s.Id} value={s.Id} selected={s.Id == flow?.ScheduleId}>
                            {s.Name}
                        </MenuItem>
                    ))}
                </Select>
            </>
        );
    };

    const renderModal = () => (
        <Dialog id="schedule-modal" open={showModal} onClose={hide}>
            <ScheduleAddEdit
                scheduleId={flow?.ScheduleId || ""}
                companyId={flow?.CompanyId}
                isScheduleTemplate={
                    currentSelection && "IsScheduleTemplate" in currentSelection
                        ? currentSelection.IsScheduleTemplate
                        : !!selectedTemplate
                }
                isNameSyncWithFlow={selectedSchedule?.IsNameSyncWithFlow}
                isExportModal={true}
                onSaveCallback={handleScheduleSaved}
            />
        </Dialog>
    );

    const renderEditBtnTooltip = () => {
        if (!flow || flow.FlowId <= 0) {
            return "Please save your flow before creating a schedule";
        }
        if (!selectedSchedule) {
            return "Please select a schedule from the dropdown to edit";
        }
        if (selectedSchedule.IsScheduleTemplate) {
            return "Copy and edit schedule";
        }
        return "Edit schedule";
    };

    // ── New behavior: template selector + summary chip
    if (useScheduleTemplates) {
        // Lock the dropdown only when a real (non-template) schedule has already been created for this
        // flow via a previous deployment. A template selection alone (selectedSchedule is null) must
        // not lock the dropdown — the user should be free to switch templates before re-deploying.
        const hasDeployedRealSchedule = isDeployed && !!selectedSchedule && !selectedSchedule.IsScheduleTemplate;
        const isDisabled = !canEdit || !!(flow && flow.IsLocked) || hasDeployedRealSchedule;
        const scheduleSummary = currentSelection ? buildScheduleSummary(currentSelection) : "";

        // Merge flowSchedules and scheduleTemplates, then deduplicate:
        // remove templates that share a name or ID with an existing flow schedule.
        const flowScheduleNames = new Set(activationDestinationSchedules.map(s => s.Name));
        const flowScheduleIds = new Set(activationDestinationSchedules.map(s => s.Id));
        const dedupedTemplates = (scheduleTemplates as IScheduleTemplate[]).filter(
            s => !flowScheduleNames.has(s.ScheduleName) && !flowScheduleIds.has(s.Id)
        );
        const thisFlowScheduleIds = activationDestinationSchedules.filter(s => s.Id == flow.ScheduleId);
        const mergedSchedules: (IScheduleTemplate | ISchedule)[] = [...dedupedTemplates, ...thisFlowScheduleIds];
        return (
            <FormControl fullWidth>
                <div style={{ display: "flex" }}>
                    <InputLabel htmlFor="schedule-template-select">Schedule Template</InputLabel>
                    <Select
                        id="schedule-template-select"
                        value={currentSelection?.Id || ""}
                        disabled={isDisabled}
                        fullWidth
                        onChange={e => handleTemplateScheduleChange(e.target.value as string)}
                    >
                        <MenuItem key={NIL_UUID} value={NIL_UUID} />
                        {mergedSchedules.map(s => (
                            <MenuItem key={s.Id} value={s.Id}>
                                {"ScheduleName" in s ? s.ScheduleName : s.Name}
                            </MenuItem>
                        ))}
                    </Select>
                    {enabledFeatures.includes("edit-activation-schedules-modal") && (
                        <Tooltip title={renderEditBtnTooltip()}>
                            <div style={{ marginTop: "16px" }}>
                                <Button
                                    disabled={!flow || flow.FlowId <= 0 || !flow.ScheduleId || !selectedSchedule}
                                    color="primary"
                                    onClick={openEditScheduleModal}
                                >
                                    <i
                                        className="material-icons"
                                        style={{ height: "30px", width: "30px", fontSize: "15px", paddingTop: "8px" }}
                                    >
                                        edit
                                    </i>
                                </Button>
                            </div>
                        </Tooltip>
                    )}
                </div>

                {scheduleSummary ? (
                    <div style={{ marginTop: "8px" }}>
                        <Chip
                            label={scheduleSummary}
                            size="small"
                            icon={
                                <i className="material-icons" style={{ fontSize: "16px" }}>
                                    schedule
                                </i>
                            }
                            style={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 500 }}
                        />
                    </div>
                ) : (
                    <div style={{ marginTop: "8px", color: "#666", fontSize: "0.875rem" }}>
                        Schedules are optional. Add one here for automatic audience refreshes.
                    </div>
                )}

                <RecurrencePopup
                    open={showRecurrence}
                    onClose={() => setShowRecurrence(false)}
                    onApply={handleRecurrenceApply}
                    initialConfig={buildInitialRecurrenceConfig()}
                    disabled={isDisabled}
                />
                {enabledFeatures.includes("edit-activation-schedules-modal") && renderEditScheduleModal()}
            </FormControl>
        );
    }

    // ── Legacy behavior: grouped dropdown + edit button ──
    return (
        <FormControl fullWidth required>
            <div style={{ display: "flex" }}>
                {renderSchedules()}
                {enabledFeatures.includes("edit-activation-schedules-modal") && (
                    <Tooltip title={renderEditBtnTooltip()}>
                        <div style={{ marginTop: "16px" }}>
                            <Button
                                disabled={!flow || flow.FlowId <= 0 || !flow.ScheduleId || !selectedSchedule}
                                color="primary"
                                onClick={openEditScheduleModal}
                            >
                                <i
                                    className="material-icons"
                                    style={{ height: "30px", width: "30px", fontSize: "15px", paddingTop: "8px" }}
                                >
                                    edit
                                </i>
                            </Button>
                        </div>
                    </Tooltip>
                )}
            </div>
            {enabledFeatures.includes("edit-activation-schedules-modal") && renderEditScheduleModal()}
            {enabledFeatures.includes("edit-activation-schedules-modal") &&
                enabledFeatures.includes("schedule-templates") &&
                renderModal()}
        </FormControl>
    );
};

export default ActivationDestinationSchedules;
